import { Button } from '@invent/wl-ui-kit-next/ui';
import * as React from 'react';

/** A fresh lazy component per attempt: `React.lazy` keeps a rejected import. */
const lazyEditor = () =>
  React.lazy(() => import(/* webpackChunkName: "placeholder-editor" */ './placeholder-editor'));

const lazyRichEditor = () => React.lazy(() => import(/* webpackChunkName: "rich-editor" */ './rich-editor'));

/**
 * A template part's editor, as a lazy chunk, so TipTap — and, for a rich part, the Maily compiler —
 * reach only the browser that opens Setup → Templates: a line part's field with its placeholders
 * as pills (`placeholder-editor.tsx`), or a rich part's block editor (`rich-editor.tsx`). While it
 * loads, a box of the field's height stands in; a chunk that does not arrive is said, with "Try
 * again".
 */
export function PlaceholderField(props: React.ComponentProps<ReturnType<typeof lazyEditor>>) {
  const height = props.multiline ? (props.tall ? 'min-h-32' : 'min-h-20') : 'min-h-9';
  return <Lazy load={lazyEditor} height={height} props={props} />;
}

export function RichField(props: React.ComponentProps<ReturnType<typeof lazyRichEditor>>) {
  return <Lazy load={lazyRichEditor} height="min-h-24" props={props} />;
}

function Lazy<P extends object>({
  load,
  height,
  props
}: {
  load: () => React.ComponentType<P>;
  height: string;
  props: P;
}) {
  const [attempt, setAttempt] = React.useState(0);
  const Editor = React.useMemo(load, [attempt, load]);
  return (
    <ChunkBoundary key={attempt} onRetry={() => setAttempt((n) => n + 1)}>
      <React.Suspense fallback={<div className={`${height} rounded-lg border border-input bg-muted/30`} />}>
        <Editor {...props} />
      </React.Suspense>
    </ChunkBoundary>
  );
}

class ChunkBoundary extends React.Component<
  { onRetry: () => void; children: React.ReactNode },
  { failed: boolean }
> {
  state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  render() {
    if (!this.state.failed) return this.props.children;
    return (
      <div role="alert" className="flex flex-wrap items-center gap-2 text-sm">
        <span>The editor could not be loaded.</span>
        <Button type="button" variant="outline" size="sm" onClick={this.props.onRetry}>
          Try again
        </Button>
      </div>
    );
  }
}
